import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";

const TARGET_OPTIONS = [
  "Hotels",
  "Resorts",
  "Freizeitflächen",
  "Eventlocations",
  "Immobilien",
  "Tourismus",
  "Investoren",
  "Sonstige",
];

const INITIAL = {
  name: "",
  company: "",
  email: "",
  phone: "",
  region: "",
  industry_network: "",
  target_audiences: "",
  sales_experience: "",
  message: "",
  consent: false,
};

export default function SalesPartnerForm() {
  const navigate = useNavigate();
  const [data, setData] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const selected = (data.target_audiences || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

  const toggle = (item) => {
    const next = selected.includes(item)
      ? selected.filter((x) => x !== item)
      : [...selected, item];
    set("target_audiences", next.join(", "));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!data.consent) {
      toast.error("Bitte Datenschutzhinweis bestätigen");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/sales-partners", data);
      navigate("/danke");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Anfrage fehlgeschlagen");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="surface p-6 md:p-10" data-testid="sales-partner-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label className="field-label">Name *</label>
          <input
            required
            className="field"
            value={data.name}
            onChange={(e) => set("name", e.target.value)}
            data-testid="input-name"
          />
        </div>
        <div>
          <label className="field-label">Unternehmen (optional)</label>
          <input
            className="field"
            value={data.company}
            onChange={(e) => set("company", e.target.value)}
          />
        </div>
        <div>
          <label className="field-label">E-Mail *</label>
          <input
            required
            type="email"
            className="field"
            value={data.email}
            onChange={(e) => set("email", e.target.value)}
            data-testid="input-email"
          />
        </div>
        <div>
          <label className="field-label">Telefon</label>
          <input
            className="field"
            value={data.phone}
            onChange={(e) => set("phone", e.target.value)}
          />
        </div>
        <div>
          <label className="field-label">Region</label>
          <input
            className="field"
            value={data.region}
            onChange={(e) => set("region", e.target.value)}
            placeholder="z. B. NRW, Süddeutschland, DACH"
          />
        </div>
        <div>
          <label className="field-label">Branche / Netzwerk</label>
          <input
            className="field"
            value={data.industry_network}
            onChange={(e) => set("industry_network", e.target.value)}
          />
        </div>
      </div>

      <div className="mt-6">
        <label className="field-label">Zugang zu welchen Zielgruppen?</label>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 mt-2">
          {TARGET_OPTIONS.map((t) => {
            const active = selected.includes(t);
            return (
              <button
                key={t}
                type="button"
                onClick={() => toggle(t)}
                data-testid={`sales-target-${t.toLowerCase()}`}
                className={`text-left px-4 py-3 border text-sm transition-colors ${
                  active
                    ? "border-[#18E096] bg-[#18E096]/10 text-white"
                    : "border-[rgba(255,255,255,0.12)] text-[#AEB7B7] hover:border-[#18E096]/60 hover:text-white"
                }`}
              >
                {t}
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-6">
        <div>
          <label className="field-label">B2B-Vertriebserfahrung</label>
          <input
            className="field"
            value={data.sales_experience}
            onChange={(e) => set("sales_experience", e.target.value)}
            placeholder="kurz beschreiben"
          />
        </div>
        <div>
          <label className="field-label">Nachricht</label>
          <input
            className="field"
            value={data.message}
            onChange={(e) => set("message", e.target.value)}
          />
        </div>
      </div>

      <div className="hairline my-8" />

      <label className="flex items-start gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={data.consent}
          onChange={(e) => set("consent", e.target.checked)}
          className="mt-1 accent-[#18E096] w-4 h-4"
          data-testid="input-consent"
        />
        <span className="text-sm text-[#AEB7B7] leading-relaxed">
          Ich bin damit einverstanden, dass meine Angaben zur Prüfung meines Sales-Partner-Profils
          verarbeitet werden. Die konkrete Abwicklung wird vorab individuell bestätigt.
        </span>
      </label>

      <div className="mt-8">
        <button
          type="submit"
          disabled={submitting}
          className="btn-primary disabled:opacity-50"
          data-testid="sales-submit"
        >
          {submitting ? (
            <>
              <Loader2 size={16} strokeWidth={1.5} className="animate-spin" /> Wird gesendet…
            </>
          ) : (
            <>
              Als Sales Partner anfragen <ArrowUpRight size={16} strokeWidth={1.5} />
            </>
          )}
        </button>
      </div>
    </form>
  );
}
